import { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";
import { enUS, ar } from "date-fns/locale";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Clock, Layers, Home, Building2, Calendar, MapPin, CheckCircle2, ChevronLeft, ChevronRight, Minus, Plus, Wallet, Tag, Sparkles, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MobileSelect, MobileSelectItem } from "@/components/ui/mobile-select";
import { z } from "zod";
import { useServices } from "@/hooks/use-services";
import { useCreateOrder } from "@/hooks/use-orders";
import { useGovernorates, useDistricts, useDeliveryFee, useAvailableTimeSlots, useTimeSlotsForServices } from "@/hooks/use-locations";
import { useChaletAvailability } from "@/hooks/use-chalet-availability";
import { useAddresses, useCreateAddress } from "@/hooks/use-addresses";
import { useWalletBalance } from "@/hooks/use-wallet";
import { useIsAuthenticated, getCurrentUser } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { apiClient } from "@/lib/api-client";
import type { Service, CreateOrderDto } from "@/lib/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type ServiceCategory = "hourly" | "deep" | "chalet" | null;
type PropertyType = "house" | "apartment" | null;

// ---------------------------------------------------------------------------
// Form schema (shared checkout fields)
// ---------------------------------------------------------------------------
const formSchema = z.object({
  governorateId: z.string().min(1, "governorateRequired"),
  districtId: z.string().min(1, "districtRequired"),
  notes: z.string().optional(),
  block: z.string().optional(),
  avenue: z.string().optional(),
  street: z.string().optional(),
  building: z.string().optional(),
  landmark: z.string().optional(),
  saveAddress: z.boolean().optional(),
  addressLabel: z.string().optional(),
});
type FormValues = z.infer<typeof formSchema>;

export default function Booking() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { toast } = useToast();
  const isAuthenticated = useIsAuthenticated();
  const currentUser = getCurrentUser();

  // ----- Data hooks -----
  const { data: services } = useServices();
  const { data: governorates } = useGovernorates();
  const { data: addresses } = useAddresses();
  const { data: walletData } = useWalletBalance();
  const createOrder = useCreateOrder();
  const createAddress = useCreateAddress();
  const walletBalance = walletData?.balance ?? 0;

  // ----- Service-type selection (nothing preselected; user must choose) -----
  const [category, setCategory] = useState<ServiceCategory>(null);
  const [propertyType, setPropertyType] = useState<PropertyType>(null);

  // Validation: surface red highlights only after the user attempts to confirm.
  // Each flag gates one step's error display so we only highlight what's relevant.
  const [touchedConfig, setTouchedConfig] = useState(false);
  const [touchedScheduling, setTouchedScheduling] = useState(false);
  const [touchedLocation, setTouchedLocation] = useState(false);
  const showError = (cond: boolean) => touchedConfig && cond;

  // Resolve the matching Service object(s) from the catalog by pricingType.
  const mainServices = useMemo(
    () => (services ?? []).filter((s) => s.isActive && !s.isAddOn),
    [services]
  );
  const hourlyService = useMemo(
    () => mainServices.find((s) => s.pricingType === "hourly"),
    [mainServices]
  );
  const deepHouseService = useMemo(
    () => mainServices.find((s) => s.pricingType === "deep_house"),
    [mainServices]
  );
  // Apartment pricing is a lookup table — until the admin adds at least one
  // row the service can't produce a price, so treat it as unavailable.
  const deepApartmentService = useMemo(
    () => mainServices.find((s) => s.pricingType === "deep_apartment" && (s.apartmentPriceItems?.length ?? 0) > 0),
    [mainServices]
  );
  const chaletService = useMemo(
    () => mainServices.find((s) => s.pricingType === "chalet"),
    [mainServices]
  );

  // The active service for the chosen category (deep depends on propertyType).
  const activeService: Service | undefined = useMemo(() => {
    if (category === null) return undefined;
    if (category === "hourly") return hourlyService;
    if (category === "chalet") return chaletService;
    // category === "deep" — requires a property type to resolve
    if (propertyType === "house") return deepHouseService;
    if (propertyType === "apartment") return deepApartmentService;
    return undefined;
  }, [category, propertyType, hourlyService, chaletService, deepHouseService, deepApartmentService]);

  // ----- Type-specific config state (all start null — nothing preselected) -----
  const [hours, setHours] = useState<number | null>(null);
  const [workers, setWorkers] = useState<number | null>(null);
  const [floors, setFloors] = useState<number | null>(null);
  const [aptRooms, setAptRooms] = useState<number | null>(null);
  const [aptBathrooms, setAptBathrooms] = useState<number | null>(null);
  const [aptLivingRooms, setAptLivingRooms] = useState<number | null>(null);
  const [aptKitchens, setAptKitchens] = useState<number | null>(null);
  const [isFurnished, setIsFurnished] = useState<boolean | null>(null);
  const [chaletRooms, setChaletRooms] = useState<number | null>(null);
  const [selectedAddOns, setSelectedAddOns] = useState<Record<string, number>>({});

  // Reset add-ons whenever the service changes (different services have different add-ons).
  useEffect(() => { setSelectedAddOns({}); }, [activeService?.id]);

  // Read query params (from the Home widget) to pre-select category + values.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const num = (key: string) => {
      const v = params.get(key);
      const n = v ? parseInt(v, 10) : NaN;
      return Number.isFinite(n) ? n : undefined;
    };
    const typeParam = params.get("type");
    if (typeParam === "hourly" || typeParam === "deep" || typeParam === "chalet") {
      setCategory(typeParam);
    }
    // Hourly
    const h = num("hours"); if (h != null) setHours(h);
    const w = num("workers"); if (w != null) setWorkers(w);
    // Deep
    const prop = params.get("property");
    if (prop === "house" || prop === "apartment") setPropertyType(prop);
    const fl = num("floors"); if (fl != null) setFloors(fl);
    const r = num("rooms"); if (r != null) {
      setAptRooms(r);
      setChaletRooms(r);
    }
    const b = num("bathrooms"); if (b != null) setAptBathrooms(b);
    const lr = num("livingRooms"); if (lr != null) setAptLivingRooms(lr);
    const k = num("kitchens"); if (k != null) setAptKitchens(k);
    const furn = params.get("furnished");
    if (furn === "true") setIsFurnished(true);
    if (furn === "false") setIsFurnished(false);
  }, []);

  // ----- Add-ons available for the active service -----
  const addOnServices = useMemo(() => {
    if (!activeService?.availableAddOns || !services) return [];
    return services.filter((s) => s.isAddOn && activeService.availableAddOns!.includes(s.id));
  }, [activeService, services]);

  // ----- Scheduling state -----
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [timeSlotId, setTimeSlotId] = useState<string>("");
  const [chaletDate, setChaletDate] = useState<string>("");

  // Type-specific inputs (sent both to the availability endpoint and the order payload)
  const serviceInputs = useMemo(() => {
    const inputs: Record<string, unknown> = {};
    if (category === "hourly") { inputs.hours = hours; inputs.workers = workers; }
    else if (category === "deep" && propertyType === "house") { inputs.floors = floors; }
    else if (category === "deep" && propertyType === "apartment") {
      inputs.rooms = aptRooms ?? 0; inputs.bathrooms = aptBathrooms ?? 0;
      inputs.livingRooms = aptLivingRooms ?? 0; inputs.kitchens = aptKitchens ?? 0; inputs.isFurnished = isFurnished;
    } else if (category === "chalet") { inputs.rooms = chaletRooms; }
    return inputs;
  }, [category, propertyType, hours, workers, floors, aptRooms, aptBathrooms, aptLivingRooms, aptKitchens, isFurnished, chaletRooms]);

  // Lookup-table row matching the selected apartment combination (exact match
  // on all four counts). Undefined = the combination has no configured price.
  const aptPriceItem = useMemo(() => {
    if (category !== "deep" || propertyType !== "apartment" || !deepApartmentService) return undefined;
    return (deepApartmentService.apartmentPriceItems ?? []).find(
      (it) =>
        it.rooms === (aptRooms ?? 0) &&
        it.bathrooms === (aptBathrooms ?? 0) &&
        it.livingRooms === (aptLivingRooms ?? 0) &&
        it.kitchens === (aptKitchens ?? 0)
    );
  }, [category, propertyType, deepApartmentService, aptRooms, aptBathrooms, aptLivingRooms, aptKitchens]);

  // ----- Step gating (needed before the slot hooks so we never fetch
  //       availability for a configuration the backend would reject) -----
  const configValid = useMemo(() => {
    if (!activeService) return false;
    if (category === "hourly") {
      return hours != null && workers != null
        && hours >= (activeService.minHours ?? 1)
        && workers >= (activeService.minWorkers ?? 1);
    }
    if (category === "deep" && propertyType === "house") return floors != null && floors >= (activeService.minFloors ?? 1);
    if (category === "deep" && propertyType === "apartment") {
      const units = (aptRooms ?? 0) + (aptBathrooms ?? 0) + (aptLivingRooms ?? 0) + (aptKitchens ?? 0);
      return units > 0 && isFurnished != null && aptPriceItem != null;
    }
    if (category === "chalet") return chaletRooms != null && chaletRooms >= 1;
    return false;
  }, [activeService, category, propertyType, hours, workers, floors, aptRooms, aptBathrooms, aptLivingRooms, aptKitchens, isFurnished, chaletRooms, aptPriceItem]);

  const dateStr = category === "chalet" || !configValid ? "" : selectedDate;

  // Items sent to the availability endpoint so the backend can reserve the right
  // number of consecutive slots based on the service's actual duration.
  const itemsForAvailability = useMemo(() => {
    if (!activeService || category === "chalet") return [];
    const addOns = Object.entries(selectedAddOns)
      .filter(([, qty]) => qty > 0)
      .map(([addOnId, quantity]) => ({ addOnId, quantity }));
    return [{
      serviceId: activeService.id,
      quantity: 1,
      inputs: serviceInputs,
      ...(addOns.length > 0 ? { addOns } : {}),
    }];
  }, [activeService, category, selectedAddOns, serviceInputs]);

  // All slots for the date (full time grid)
  const { data: allSlots, isLoading: allSlotsLoading } = useAvailableTimeSlots(dateStr || undefined);
  // Slots that can fit the full booking duration (duration-aware)
  const { data: fittingSlots, isLoading: fittingSlotsLoading } = useTimeSlotsForServices(
    dateStr || undefined,
    itemsForAvailability
  );
  const slotsLoading = allSlotsLoading || fittingSlotsLoading;
  const fittingSlotIds = useMemo(() => new Set((fittingSlots ?? []).map((s) => s.id)), [fittingSlots]);
  const timeSlots = allSlots;

  // Chalet: fetch current + next calendar month to cover the next ~30 days
  const chaletMonths = useMemo(() => {
    const now = new Date();
    const m1 = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const m2 = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`;
    return [m1, m2];
  }, []);
  const chaletEnabled = category === "chalet" ? chaletService?.id : undefined;

  const { data: chaletAvail1 } = useChaletAvailability(chaletEnabled, chaletEnabled ? chaletMonths[0] : undefined);
  const { data: chaletAvail2 } = useChaletAvailability(chaletEnabled, chaletEnabled ? chaletMonths[1] : undefined);

  // Merge both months into one list of available upcoming dates (within next 30 days)
  const chaletAvailableDates = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() + 30);
    const cutoffStr = cutoff.toISOString().split("T")[0];
    return [...(chaletAvail1 ?? []), ...(chaletAvail2 ?? [])]
      .filter((d) => d.date >= today && d.date <= cutoffStr && d.available && d.isEnabled);
  }, [chaletAvail1, chaletAvail2]);

  // ----- Form (checkout fields) -----
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      governorateId: "",
      districtId: "",
      notes: "",
      block: "",
      avenue: "",
      street: "",
      building: "",
      landmark: "",
      saveAddress: false,
      addressLabel: "",
    },
  });

  const watchedGov = form.watch("governorateId");
  const { data: districts } = useDistricts(watchedGov || undefined);
  const [selectedDistrictId, setSelectedDistrictId] = useState("");
  const { data: deliveryFeeData } = useDeliveryFee(selectedDistrictId || undefined);
  const deliveryFee = deliveryFeeData?.deliveryFee || 0;

  // Reset district when the user manually changes the governorate.
  // (Address auto-select sets governorate+district together, so we skip the reset
  //  in that case to avoid clobbering the district we just set.)
  const [addressSyncing, setAddressSyncing] = useState(false);
  useEffect(() => {
    if (addressSyncing) {
      setAddressSyncing(false);
      return;
    }
    form.setValue("districtId", "");
    setSelectedDistrictId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedGov]);

  // ----- Address mode -----
  const [useManualAddress, setUseManualAddress] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [showAccountBlocked, setShowAccountBlocked] = useState(false);

  // Select a saved address: id, form fields and the district that drives the
  // logistics fee must always move together, or the fee ends up describing an
  // address that is no longer selected.
  const selectSavedAddress = (addr: { id: string; governorateId: string; districtId: string }) => {
    setAddressSyncing(true);
    setSelectedAddressId(addr.id);
    form.setValue("governorateId", addr.governorateId);
    form.setValue("districtId", addr.districtId);
    setSelectedDistrictId(addr.districtId);
  };

  const defaultAddress = () => addresses?.find((a) => a.isDefault) || addresses?.[0];

  // Auto-select the default address ONCE when addresses first load.
  // After that, respect the user's choice (saved vs manual) — don't override it.
  useEffect(() => {
    if (addresses && addresses.length > 0 && !selectedAddressId && !useManualAddress) {
      const def = defaultAddress();
      if (def) selectSavedAddress(def);
    }
    if (addresses && addresses.length === 0 && !useManualAddress) {
      setUseManualAddress(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses]);

  // ----- Pricing computation -----
  const { basePrice, estimatedDurationMin, workersCount } = useMemo(() => {
    const svc = activeService;
    if (!svc) return { basePrice: 0, estimatedDurationMin: 0, workersCount: 0 };
    if (category === "hourly" && svc.hourlyRate != null) {
      const h = hours ?? 0;
      const w = workers ?? 0;
      return { basePrice: svc.hourlyRate * h * w, estimatedDurationMin: h * 60, workersCount: w };
    }
    if (category === "deep" && propertyType === "house" && svc.pricePerFloor != null) {
      // Fixed crew: more floors means more time, NOT more workers
      const f = floors ?? 0;
      return { basePrice: svc.pricePerFloor * f, estimatedDurationMin: (svc.durationPerFloorMin ?? 0) * f, workersCount: svc.workersPerFloor ?? svc.workersPerBooking ?? 0 };
    }
    if (category === "deep" && propertyType === "apartment") {
      // Everything comes straight from the matching lookup-table row.
      if (!aptPriceItem || isFurnished == null) return { basePrice: 0, estimatedDurationMin: 0, workersCount: 0 };
      const variant = isFurnished ? aptPriceItem.furnished : aptPriceItem.unfurnished;
      return { basePrice: variant.price, estimatedDurationMin: variant.durationMin, workersCount: variant.workers };
    }
    if (category === "chalet") {
      const cr = chaletRooms ?? 0;
      const tier = (svc.priceTiers ?? []).find((ti) => cr >= ti.minRooms && cr <= ti.maxRooms);
      return { basePrice: tier?.price ?? 0, estimatedDurationMin: 0, workersCount: 0 };
    }
    return { basePrice: 0, estimatedDurationMin: 0, workersCount: 0 };
  }, [activeService, category, propertyType, hours, workers, floors, aptPriceItem, isFurnished, chaletRooms]);

  const addOnTotal = useMemo(
    () => addOnServices.reduce((sum, a) => sum + (a.price ?? 0) * (selectedAddOns[a.id] ?? 0), 0),
    [addOnServices, selectedAddOns]
  );

  const subtotal = basePrice + addOnTotal;
  const subtotalWithDelivery = subtotal + deliveryFee;

  // ----- Coupon -----
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponValidating, setCouponValidating] = useState(false);

  const validateCoupon = async () => {
    if (!couponCode.trim() || !activeService) return;
    setCouponValidating(true);
    try {
      const res = await apiClient.post("/coupons/validate", {
        code: couponCode.trim().toUpperCase(),
        orderAmount: subtotalWithDelivery,
        serviceIds: [activeService.id],
        governorateId: form.getValues("governorateId"),
        orderItems: [{ serviceId: activeService.id, subtotal }],
      });
      const data = await res.json();
      if (data.valid) {
        setCouponDiscount(data.discount || 0);
        setCouponApplied(true);
        toast({ title: t("booking.coupon.valid"), description: `${t("booking.coupon.discount")}: ${data.discount}` });
      } else {
        setCouponApplied(false);
        setCouponDiscount(0);
        toast({ title: t("booking.coupon.invalid"), description: data.message || "", variant: "destructive" });
      }
    } catch {
      toast({ title: t("booking.coupon.invalid"), variant: "destructive" });
    } finally {
      setCouponValidating(false);
    }
  };

  // Clear coupon when inputs change.
  useEffect(() => {
    setCouponApplied(false);
    setCouponDiscount(0);
  }, [subtotal, selectedDistrictId]);

  const totalPrice = Math.max(0, subtotalWithDelivery - couponDiscount);

  // ----- Wallet credit -----
  const [useWalletCredit, setUseWalletCredit] = useState(false);
  const [customWalletAmount, setCustomWalletAmount] = useState("");
  const maxWalletCredit = Math.min(walletBalance, totalPrice);
  const parsedCustomAmount = parseFloat(customWalletAmount) || 0;
  const walletCreditUsed = useWalletCredit
    ? customWalletAmount.trim() !== ""
      ? Math.min(Math.max(0, parsedCustomAmount), maxWalletCredit)
      : maxWalletCredit
    : 0;
  const cashAmountDue = totalPrice - walletCreditUsed;

  // The method is chosen on Tahseeel's hosted checkout page, not here.

  const schedulingValid = category === "chalet" ? !!chaletDate : !!selectedDate && !!timeSlotId;
  const locationValid = selectedAddressId
    ? true
    : useManualAddress
      ? !!(selectedDistrictId && form.watch("block") && form.watch("street") && form.watch("building"))
      : false;

  // ----- Submit -----
  const onSubmit = (data: FormValues) => {
    if (!activeService || !configValid) {
      setTouchedConfig(true);
      toast({ title: t("bookingClean.validation.typeRequired"), variant: "destructive" });
      return;
    }
    if (!schedulingValid) {
      setTouchedScheduling(true);
      toast({ title: t("bookingClean.validation.dateRequired"), variant: "destructive" });
      return;
    }
    if (!locationValid) {
      setTouchedLocation(true);
      // Surface field-level errors on the manual-address form so each missing
      // field is highlighted red (saved-address path can't reach here).
      if (useManualAddress) {
        const required: [keyof FormValues, string][] = [
          ["block", t("booking.validation.blockRequired")],
          ["street", t("booking.validation.streetRequired")],
          ["building", t("booking.validation.buildingRequired")],
        ];
        required.forEach(([k, msg]) => {
          if (!data[k]) form.setError(k, { type: "required", message: msg });
        });
        // Trigger react-hook-form validation so governorate/district FormMessages show.
        form.trigger(["governorateId", "districtId"]);
      }
      toast({ title: t("booking.validation.selectDistrict"), variant: "destructive" });
      return;
    }

    const orderData: CreateOrderDto = {
      items: [{
        serviceId: activeService.id,
        quantity: 1,
        inputs: serviceInputs,
        ...(Object.keys(selectedAddOns).length > 0 ? {
          addOns: Object.entries(selectedAddOns)
            .filter(([, qty]) => qty > 0)
            .map(([addOnId, quantity]) => ({ addOnId, quantity })),
        } : {}),
      }],
      governorateId: data.governorateId,
      districtId: data.districtId,
      customerName: currentUser?.name,
      notes: data.notes,
      ...(couponApplied && couponCode ? { couponCode } : {}),
      ...(useWalletCredit ? { useWalletCredit: true, walletCreditAmount: walletCreditUsed } : {}),
    };

    if (category === "chalet") {
      orderData.chaletDate = chaletDate;
    } else {
      orderData.timeSlotId = timeSlotId;
    }

    if (selectedAddressId && !useManualAddress) {
      orderData.addressId = selectedAddressId;
      const addr = addresses?.find((a) => a.id === selectedAddressId);
      if (addr) {
        orderData.governorateId = addr.governorateId;
        orderData.districtId = addr.districtId;
      }
    } else {
      orderData.block = data.block;
      orderData.avenue = data.avenue;
      orderData.street = data.street;
      orderData.building = data.building;
      orderData.landmark = data.landmark;
      orderData.customerAddress = `${t("booking.block")} ${data.block}, ${t("booking.street")} ${data.street}, ${t("booking.building")} ${data.building}`;
    }

    createOrder.mutate(orderData, {
      onSuccess: async (order) => {
        if (data.saveAddress && useManualAddress && data.block && data.street && data.building) {
          try {
            await createAddress.mutateAsync({
              label: data.addressLabel || "Home",
              governorateId: data.governorateId,
              districtId: data.districtId,
              block: data.block,
              avenue: data.avenue,
              street: data.street,
              building: data.building,
              landmark: data.landmark,
            });
          } catch { /* non-fatal */ }
        }
        if (order.cashAmountDue === 0 && (order.walletCreditUsed ?? 0) > 0) {
          window.location.href = `/payment/success?orderId=${order.id}`;
        } else if (order.paymentLink) {
          window.location.href = order.paymentLink;
        } else {
          window.location.href = `/payment/success?orderId=${order.id}`;
        }
      },
      onError: (error: Error) => {
        const msg = (error.message || "").toLowerCase();
        if (msg.includes("deactivated") || msg.includes("not active")) {
          setShowAccountBlocked(true);
        } else if (msg.includes("slot") || msg.includes("time slot")) {
          toast({ title: t("bookingClean.validation.slotUnavailable"), variant: "destructive" });
        } else if (msg.includes("chalet")) {
          const key = msg.includes("full") ? "bookingClean.validation.chaletFull" : "bookingClean.validation.chaletClosed";
          toast({ title: t(key), variant: "destructive" });
        } else {
          toast({ title: t("booking.toast.error"), description: error.message, variant: "destructive" });
        }
      },
    });
  };

  // ----- Helpers -----
  const fmt = (n: number) => n.toFixed(3);
  const cur = t("services.currency");
  const svcName = (s?: Service) => (s ? (isRTL ? s.nameAr : s.nameEn) : "");

  // ----- Date helpers (old-style date strip + time slots) -----
  const arabicDayNames: Record<string, string> = {
    'Sun': 'الأحد', 'Mon': 'الاثنين', 'Tue': 'الثلاثاء', 'Wed': 'الأربعاء',
    'Thu': 'الخميس', 'Fri': 'الجمعة', 'Sat': 'السبت',
  };
  const getNextDays = (days: number): Date[] => {
    const dates: Date[] = [];
    const today = new Date();
    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };
  const dateLocale = isRTL ? ar : enUS;

  const getDayName = (date: Date): string => {
    const enDayName = format(date, 'EEE', { locale: enUS });
    return isRTL ? arabicDayNames[enDayName] || enDayName : enDayName;
  };

  // Month abbreviation, localized (Arabic months when in Arabic mode).
  const getMonthLabel = (date: Date): string => format(date, 'MMM', { locale: dateLocale });

  // Auth gate
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 pb-24 flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">{t("booking.loading")}</p>
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-32 lg:pb-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold font-display mb-2">{t("bookingClean.title")}</h1>
            <p className="text-muted-foreground">{t("bookingClean.subtitle")}</p>
          </div>

          <div className="bg-card rounded-2xl p-6 shadow-lg border border-border/50">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">

                {/* ───── Step 1: Service type ───── */}
                <StepHeader icon={<Layers className="w-4 h-4 text-primary" />} title={t("bookingClean.stepType")} step={1} />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <TypeCard
                    active={category === "hourly"}
                    icon={<Clock className="w-6 h-6" />}
                    title={t("bookingClean.types.hourly")}
                    desc={t("bookingClean.types.hourlyDesc")}
                    onClick={() => {
                      if (!hourlyService) {
                        toast({ title: t("bookingClean.validation.serviceUnavailable"), variant: "destructive" });
                        return;
                      }
                      setCategory("hourly"); setTouchedConfig(true);
                    }}
                    unavailable={!hourlyService}
                    error={showError(category == null)}
                  />
                  <TypeCard
                    active={category === "deep"}
                    icon={<Home className="w-6 h-6" />}
                    title={t("bookingClean.types.deep")}
                    desc={t("bookingClean.types.deepDesc")}
                    onClick={() => {
                      if (!deepHouseService && !deepApartmentService) {
                        toast({ title: t("bookingClean.validation.serviceUnavailable"), variant: "destructive" });
                        return;
                      }
                      setCategory("deep"); setTouchedConfig(true);
                    }}
                    unavailable={!deepHouseService && !deepApartmentService}
                    error={showError(category == null)}
                  />
                  <TypeCard
                    active={category === "chalet"}
                    icon={<Building2 className="w-6 h-6" />}
                    title={t("bookingClean.types.chalet")}
                    desc={t("bookingClean.types.chaletDesc")}
                    onClick={() => {
                      if (!chaletService) {
                        toast({ title: t("bookingClean.validation.serviceUnavailable"), variant: "destructive" });
                        return;
                      }
                      setCategory("chalet"); setTouchedConfig(true);
                    }}
                    unavailable={!chaletService}
                    error={showError(category == null)}
                  />
                </div>
                {showError(category == null) && (
                  <p className="text-xs text-red-600 dark:text-red-400">{t("bookingClean.validation.typeRequired")}</p>
                )}

                {/* ───── Step 2: Service details ───── */}
                {category != null && (
                  <>
                    <StepHeader icon={<Sparkles className="w-4 h-4 text-primary" />} title={t("bookingClean.stepConfig")} step={2} />

                    {/* Deep clean: pick property type first (house/apartment) to resolve
                        the active service. Shown before the steppers, which need activeService. */}
                    {category === "deep" && (
                      <div>
                        <label className={cn("text-sm font-medium block mb-2", showError(propertyType == null) && "text-red-600 dark:text-red-400")}>{t("bookingClean.propertyType")}</label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              if (!deepHouseService) {
                                toast({ title: t("bookingClean.validation.serviceUnavailable"), variant: "destructive" });
                                return;
                              }
                              setPropertyType("house");
                            }}
                            className={cn("p-3 rounded-xl border-2 flex items-center justify-center gap-2 font-medium transition-colors", propertyType === "house" ? "border-primary bg-primary/5 text-primary" : showError(propertyType == null) ? "border-red-500 text-red-600 dark:text-red-400" : "border-border", !deepHouseService && "opacity-50")}
                          >
                            <Home className="w-5 h-5" /> {t("bookingClean.house")}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (!deepApartmentService) {
                                toast({ title: t("bookingClean.validation.serviceUnavailable"), variant: "destructive" });
                                return;
                              }
                              setPropertyType("apartment");
                            }}
                            className={cn("p-3 rounded-xl border-2 flex items-center justify-center gap-2 font-medium transition-colors", propertyType === "apartment" ? "border-primary bg-primary/5 text-primary" : showError(propertyType == null) ? "border-red-500 text-red-600 dark:text-red-400" : "border-border", !deepApartmentService && "opacity-50")}
                          >
                            <Building2 className="w-5 h-5" /> {t("bookingClean.apartment")}
                          </button>
                        </div>
                        {showError(propertyType == null) && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">{t("bookingClean.validation.typeRequired")}</p>
                        )}
                      </div>
                    )}

                    {/* Combined service notes (bullet points) — hidden when no notes apply */}
                    {category !== "chalet" && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-900/50">
                      <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                      <ul className="text-xs text-yellow-800 dark:text-yellow-200 space-y-1 list-disc ps-4">
                        {category === "deep" && (
                          <li>{t("bookingClean.deepNote")}</li>
                        )}
                        {category === "hourly" && (
                          <li>{t("bookingClean.petNote")}</li>
                        )}
                        {category === "deep" && (
                          <li>{t("bookingClean.foundationNote")}</li>
                        )}
                      </ul>
                    </div>
                    )}

                    {activeService && (
                    <ServiceConfigPanel
                      category={category}
                      propertyType={propertyType}
                      setPropertyType={setPropertyType}
                      service={activeService}
                      hours={hours} setHours={setHours}
                      workers={workers} setWorkers={setWorkers}
                      floors={floors} setFloors={setFloors}
                      aptRooms={aptRooms} setAptRooms={setAptRooms}
                      aptBathrooms={aptBathrooms} setAptBathrooms={setAptBathrooms}
                      aptLivingRooms={aptLivingRooms} setAptLivingRooms={setAptLivingRooms}
                      aptKitchens={aptKitchens} setAptKitchens={setAptKitchens}
                      isFurnished={isFurnished} setIsFurnished={setIsFurnished}
                      chaletRooms={chaletRooms} setChaletRooms={setChaletRooms}
                      addOnServices={addOnServices}
                      selectedAddOns={selectedAddOns}
                      setAddOnQty={(id, qty) =>
                        setSelectedAddOns((prev) => {
                          const next = { ...prev };
                          if (qty <= 0) delete next[id];
                          else next[id] = qty;
                          return next;
                        })
                      }
                      showError={showError}
                      t={t}
                      isRTL={isRTL}
                      cur={cur}
                      fmt={fmt}
                    />
                    )}
                  </>
                )}

                {/* ───── Step 3: Date & time / Chalet date ───── */}
                {configValid && (
                  <>
                    <StepHeader icon={<Calendar className="w-4 h-4 text-primary" />} title={t("bookingClean.stepDateTime")} step={3} />
                    {category === "chalet" ? (
                      <div className="space-y-3">
                        <label className={cn("text-sm font-medium block", touchedScheduling && !chaletDate && "text-red-600 dark:text-red-400")}>{t("booking.preferredDate")}</label>
                        {/* Horizontal date strip — upcoming available dates (next 30 days) */}
                        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                          {chaletAvailableDates.length === 0 && (
                            <p className="text-sm text-muted-foreground py-4">{t("bookingClean.noAvailability")}</p>
                          )}
                          {chaletAvailableDates.map(({ date: dateStr }) => {
                            const d = new Date(dateStr);
                            const dayName = getDayName(d);
                            const dayNumber = format(d, 'd');
                            const monthLabel = getMonthLabel(d);
                            const isSelected = chaletDate === dateStr;
                            const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');
                            return (
                              <button
                                key={dateStr}
                                type="button"
                                onClick={() => setChaletDate(dateStr)}
                                className={cn(
                                  "flex-shrink-0 flex flex-col items-center justify-center p-3 rounded-xl border-2 min-w-[70px] transition-all",
                                  isSelected
                                    ? "border-primary bg-primary/10 text-primary"
                                    : "border-border hover:border-primary/50 hover:bg-muted/50"
                                )}
                              >
                                <span className="text-xs font-medium">{isToday ? t('booking.today') : dayName}</span>
                                <span className="text-lg font-semibold">{dayNumber}</span>
                                <span className="text-[10px] text-muted-foreground">{monthLabel}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Date strip — next 14 days */}
                        <div>
                          <label className={cn("text-sm font-medium block mb-2", touchedScheduling && !selectedDate && "text-red-600 dark:text-red-400")}>{t("booking.preferredDate")}</label>
                          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                            {getNextDays(14).map((date, index) => {
                              const dateStr = format(date, 'yyyy-MM-dd');
                              const isSelected = selectedDate === dateStr;
                              const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                              const dayName = isToday ? t('booking.today') : getDayName(date);
                              const dayNumber = format(date, 'd');
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => { setSelectedDate(dateStr); setTimeSlotId(""); }}
                                  className={cn(
                                    "flex-shrink-0 flex flex-col items-center justify-center p-3 rounded-xl border-2 min-w-[70px] transition-all",
                                    isSelected
                                      ? "border-primary bg-primary/10 text-primary"
                                      : "border-border hover:border-primary/50 hover:bg-muted/50"
                                  )}
                                >
                                  <span className="text-xs font-medium">{dayName}</span>
                                  <span className="text-lg font-semibold">{dayNumber}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Time slots grid */}
                        <div>
                          <label className={cn("text-sm font-medium block mb-2", touchedScheduling && !timeSlotId && "text-red-600 dark:text-red-400")}>{t("booking.preferredTime")}</label>
                          {touchedScheduling && !timeSlotId && selectedDate && (
                            <p className="text-xs text-red-600 dark:text-red-400 mb-2">{t("bookingClean.validation.timeRequired")}</p>
                          )}
                          {!selectedDate ? (
                            <div className="text-center text-sm text-muted-foreground py-4">
                              {t("booking.selectDateFirst")}
                            </div>
                          ) : slotsLoading ? (
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                              {[1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className="flex flex-col items-center justify-center p-3 rounded-xl border-2 animate-pulse bg-muted">
                                  <div className="h-4 w-12 bg-muted-foreground/20 rounded mb-1"></div>
                                  <div className="h-3 w-10 bg-muted-foreground/20 rounded"></div>
                                </div>
                              ))}
                            </div>
                          ) : (timeSlots ?? []).length === 0 ? (
                            <div className="text-center text-sm text-muted-foreground py-4">
                              {t("booking.noTimeSlots")}
                            </div>
                          ) : (
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                              {(timeSlots ?? []).map((slot) => {
                                const isSelected = timeSlotId === slot.id;
                                const full = (slot.availableSpots ?? 1) <= 0;
                                const fits = fittingSlotIds.has(slot.id);
                                const closed = full || !fits;
                                return (
                                  <button
                                    key={slot.id}
                                    type="button"
                                    disabled={closed}
                                    onClick={() => setTimeSlotId(slot.id)}
                                    className={cn(
                                      "flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all",
                                      isSelected
                                        ? "border-primary bg-primary/10 text-primary"
                                        : closed
                                          ? "border-red-200 bg-red-50/50 text-red-400 cursor-not-allowed opacity-60 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-500"
                                          : "border-border hover:border-primary/50 hover:bg-muted/50"
                                    )}
                                  >
                                    <span className="text-sm font-medium">{slot.time}</span>
                                    <span className="text-xs text-muted-foreground">{slot.endTime}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ───── Step 4: Location ───── */}
                {configValid && schedulingValid && (
                  <>
                    <StepHeader icon={<MapPin className="w-4 h-4 text-primary" />} title={t("bookingClean.stepLocation")} step={4} />
                    {touchedLocation && !locationValid && (
                      <p className="text-xs text-red-600 dark:text-red-400">{t("booking.validation.selectDistrict")}</p>
                    )}
                    {/* Address mode toggle */}
                    {addresses && addresses.length > 0 && (
                      <div className="flex gap-2 mb-4">
                        <Button
                          type="button"
                          variant={!useManualAddress ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setUseManualAddress(false);
                            // Coming back from manual entry nothing is selected —
                            // restore the default address so the mode is never
                            // active with no address and no fee.
                            if (!selectedAddressId) {
                              const def = defaultAddress();
                              if (def) selectSavedAddress(def);
                            }
                          }}
                        >
                          {t("booking.address")}
                        </Button>
                        <Button
                          type="button"
                          variant={useManualAddress ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setUseManualAddress(true);
                            setSelectedAddressId("");
                            // Clear the district too, otherwise the logistics fee
                            // keeps showing for the address we just deselected.
                            form.setValue("governorateId", "");
                            form.setValue("districtId", "");
                            setSelectedDistrictId("");
                          }}
                        >
                          {t("booking.location")}
                        </Button>
                      </div>
                    )}

                    {!useManualAddress && addresses && addresses.length > 0 ? (
                      <div className="space-y-2">
                        {addresses.map((addr) => (
                          <button
                            key={addr.id}
                            type="button"
                            onClick={() => selectSavedAddress(addr)}
                            className={cn(
                              "w-full text-start p-4 rounded-xl border transition-colors",
                              selectedAddressId === addr.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                            )}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{addr.label}</p>
                                <p className="text-sm text-muted-foreground">
                                  {isRTL ? addr.governorate?.nameAr : addr.governorate?.nameEn} - {isRTL ? addr.district?.nameAr : addr.district?.nameEn}
                                </p>
                                <p className="text-xs text-muted-foreground">{t("booking.block")} {addr.block}, {t("booking.street")} {addr.street}, {t("booking.building")} {addr.building}</p>
                              </div>
                              {selectedAddressId === addr.id && <CheckCircle2 className="h-5 w-5 text-primary" />}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="governorateId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("booking.governorate")}</FormLabel>
                              <FormControl>
                                <MobileSelect
                                  value={field.value}
                                  onValueChange={(v) => { field.onChange(v); setSelectedDistrictId(""); }}
                                  placeholder={t("booking.selectGovernorate")}
                                >
                                  {(governorates ?? []).map((g) => (
                                    <MobileSelectItem key={g.id} value={g.id}>
                                      {isRTL ? g.nameAr : g.nameEn}
                                    </MobileSelectItem>
                                  ))}
                                </MobileSelect>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="districtId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("booking.district")}</FormLabel>
                              <FormControl>
                                <MobileSelect
                                  value={field.value}
                                  onValueChange={(v) => { field.onChange(v); setSelectedDistrictId(v); }}
                                  placeholder={t("booking.selectDistrict")}
                                  disabled={!watchedGov}
                                >
                                  {(districts ?? []).map((d) => (
                                    <MobileSelectItem key={d.id} value={d.id}>
                                      {isRTL ? d.nameAr : d.nameEn}
                                    </MobileSelectItem>
                                  ))}
                                </MobileSelect>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {selectedDistrictId && (
                          <div className="flex items-center justify-between text-sm p-3 rounded-xl bg-muted/50 border border-border">
                            <span className="text-muted-foreground">{t("booking.deliveryFee")}</span>
                            <span className="font-medium">{fmt(deliveryFee)} {cur}</span>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          <FormField control={form.control} name="block" render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("booking.block")}</FormLabel>
                              <FormControl><Input {...field} placeholder={t("booking.block")} className="rounded-xl" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField control={form.control} name="street" render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("booking.street")}</FormLabel>
                              <FormControl><Input {...field} placeholder={t("booking.street")} className="rounded-xl" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField control={form.control} name="building" render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("booking.building")}</FormLabel>
                              <FormControl><Input {...field} placeholder={t("booking.building")} className="rounded-xl" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField control={form.control} name="avenue" render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("booking.avenue")}</FormLabel>
                              <FormControl><Input {...field} placeholder={t("booking.avenue")} className="rounded-xl" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>
                        <FormField control={form.control} name="landmark" render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("booking.landmark")}</FormLabel>
                            <FormControl><Input {...field} placeholder={t("booking.landmark")} className="rounded-xl" /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />
                        <FormField control={form.control} name="saveAddress" render={({ field }) => (
                          <FormItem className="flex items-center gap-2 space-y-0">
                            <FormControl>
                              <input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} className="h-4 w-4" />
                            </FormControl>
                            <FormLabel className="!mt-0">{t("booking.saveAddressForFuture")}</FormLabel>
                          </FormItem>
                        )} />
                        {form.watch("saveAddress") && (
                          <FormField control={form.control} name="addressLabel" render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("booking.fullName")}</FormLabel>
                              <FormControl><Input {...field} placeholder="Home" className="rounded-xl" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* ───── Step 5: Summary & payment ───── */}
                {configValid && schedulingValid && locationValid && (
                  <>
                    <StepHeader icon={<CheckCircle2 className="w-4 h-4 text-primary" />} title={t("bookingClean.stepSummary")} step={5} />

                    <FormField control={form.control} name="notes" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("booking.addNotes")}</FormLabel>
                        <FormControl><Input {...field} placeholder={t("booking.addNotes")} className="rounded-xl" /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />

                    {/* Summary */}
                    <div className="bg-muted/50 rounded-xl p-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{svcName(activeService)}</span>
                        <span className="font-medium">{fmt(basePrice)} {cur}</span>
                      </div>
                      {addOnTotal > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("bookingClean.equipmentAddOns")}</span>
                          <span className="font-medium">{fmt(addOnTotal)} {cur}</span>
                        </div>
                      )}
                      {deliveryFee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("booking.deliveryFee")}</span>
                          <span className="font-medium">{fmt(deliveryFee)} {cur}</span>
                        </div>
                      )}
                      {estimatedDurationMin > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("bookingClean.estDuration")}</span>
                          <span className="font-medium">{estimatedDurationMin} {t("bookingClean.min")}</span>
                        </div>
                      )}
                      {workersCount > 0 && category !== "deep" && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("bookingClean.workersLabel")}</span>
                          <span className="font-medium">{workersCount}</span>
                        </div>
                      )}
                    </div>

                    {/* Coupon */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2"><Tag className="h-4 w-4" />{t("booking.coupon.title")}</label>
                      <div className="flex gap-2">
                        <Input
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value)}
                          placeholder={t("booking.coupon.placeholder")}
                          className="rounded-xl"
                        />
                        <Button type="button" variant="outline" onClick={validateCoupon} disabled={couponValidating || !couponCode.trim()}>
                          {couponValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : couponApplied ? <CheckCircle2 className="h-4 w-4" /> : t("booking.coupon.apply")}
                        </Button>
                      </div>
                      {couponApplied && (
                        <div className="flex items-center justify-between bg-primary/10 rounded-lg px-3 py-2">
                          <span className="text-sm font-medium text-primary">{couponCode.toUpperCase()}</span>
                          <span className="text-sm font-bold text-primary">- {fmt(couponDiscount)} {cur}</span>
                        </div>
                      )}
                    </div>

                    {/* Wallet credit */}
                    {walletBalance > 0 && (
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={() => setUseWalletCredit((v) => !v)}
                          className={cn(
                            "w-full flex items-center justify-between p-3 rounded-xl border transition-colors",
                            useWalletCredit ? "border-primary bg-primary/5" : "border-border"
                          )}
                        >
                          <span className="flex items-center gap-2 text-sm">
                            <Wallet className="h-4 w-4 text-primary" />
                            {t("booking.payment.useWalletCredit")}
                            <span className="text-muted-foreground">({fmt(walletBalance)} {cur})</span>
                          </span>
                          <span className={cn("w-5 h-5 rounded border-2 flex items-center justify-center", useWalletCredit ? "bg-primary border-primary" : "border-input")}>
                            {useWalletCredit && <CheckCircle2 className="h-3 w-3 text-primary-foreground" />}
                          </span>
                        </button>
                        {useWalletCredit && (
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">{t("booking.payment.customAmount")}</label>
                            <Input
                              type="text"
                              value={customWalletAmount}
                              onChange={(e) => setCustomWalletAmount(e.target.value.replace(/[^\d.]/g, ""))}
                              placeholder={t("booking.payment.leaveEmptyForMax")}
                              className="rounded-xl"
                              inputMode="decimal"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Payment — method is chosen on the secure payment page */}
                    {cashAmountDue === 0 && useWalletCredit ? (
                      <Alert className="bg-primary/10 border-primary/30">
                        <AlertDescription className="text-primary font-medium">{t("booking.payment.fullyPaidByWallet")}</AlertDescription>
                      </Alert>
                    ) : (
                      <div className="space-y-2">
                        <label className="text-sm font-medium">{t("booking.payment.title")}</label>
                        <p className="text-xs text-muted-foreground">{t("booking.payment.chooseOnGateway")}</p>
                      </div>
                    )}

                    {/* Totals */}
                    <div className="space-y-1 pt-2 border-t border-border">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t("booking.totalPrice")}</span>
                        <span className="font-medium">{fmt(totalPrice)} {cur}</span>
                      </div>
                      {walletCreditUsed > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t("booking.payment.walletCreditApplied")}</span>
                          <span className="font-medium text-primary">- {fmt(walletCreditUsed)} {cur}</span>
                        </div>
                      )}
                      {cashAmountDue > 0 && (
                        <div className="flex justify-between text-lg font-bold pt-1">
                          <span>{t("booking.payment.amountToPay")}</span>
                          <span>{fmt(cashAmountDue)} {cur}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* ───── Always-visible Submit ─────
                     Rendered outside the step gates so the user can click it at any
                     time. If required steps are incomplete, onSubmit aborts and
                     surfaces the relevant error highlights instead of submitting. */}
                <Button type="submit" className="w-full h-12 rounded-xl text-lg font-semibold" disabled={createOrder.isPending}>
                  {createOrder.isPending ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> {t("booking.processing")}</>
                  ) : (
                    t("booking.confirmBooking")
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>

      {/* Account blocked dialog */}
      <Dialog open={showAccountBlocked} onOpenChange={setShowAccountBlocked}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("booking.toast.accountDeactivated")}</DialogTitle>
            <DialogDescription>{t("booking.toast.error")}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function StepHeader({ icon, title, step }: { icon: React.ReactNode; title: string; step: number }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">{icon}</div>
      <h3 className="font-semibold text-lg">{title}</h3>
      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{step}/5</span>
    </div>
  );
}

function TypeCard({
  active, icon, title, desc, onClick, unavailable, error,
}: { active: boolean; icon: React.ReactNode; title: string; desc: string; onClick: () => void; unavailable?: boolean; error?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "text-start p-4 rounded-xl border transition-all",
        active
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : error
            ? "border-red-500 bg-red-50/50 dark:bg-red-950/30"
            : "border-border hover:border-primary/50",
        unavailable && "opacity-50"
      )}
    >
      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-2", active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
        {icon}
      </div>
      <p className="font-semibold text-sm">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{desc}</p>
    </button>
  );
}

function Stepper({
  label, value, setValue, min, max, suffix, error, startAt,
}: {
  label: string;
  value: number | null;
  setValue: (n: number | null) => void;
  min: number;
  max: number;
  suffix?: string;
  error?: boolean;
  startAt?: number;
}) {
  // Value used on the first "+" click (when value is null). Defaults to min.
  const firstValue = startAt ?? min;
  const atMin = value == null || value <= min;
  // Treat null as 0 so a dimension whose max is 0 (e.g. no apartment price row
  // uses it) starts with "+" already disabled instead of jumping past max.
  const atMax = (value ?? 0) >= max;
  return (
    <div>
      <label className={cn("text-sm font-medium block mb-2", error && "text-red-600 dark:text-red-400")}>{label}</label>
      <div className={cn(
        "flex items-center justify-between bg-background rounded-xl border px-3 py-2",
        error ? "border-red-500 bg-red-50/50 dark:bg-red-950/30" : "border-input"
      )}>
        <button type="button" onClick={() => setValue(value == null ? null : Math.max(min, value - 1))} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed" disabled={atMin}>
          <Minus className="w-4 h-4" />
        </button>
        <span className={cn("font-bold text-lg", error && "text-red-600 dark:text-red-400")}>
          {value == null ? 0 : value}{suffix && value != null ? ` ${suffix}` : ""}
        </span>
        <button type="button" onClick={() => setValue(value == null ? firstValue : Math.min(max, value + 1))} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed" disabled={atMax}>
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Service config panel (type-specific)
// ---------------------------------------------------------------------------
interface ServiceConfigProps {
  category: ServiceCategory;
  propertyType: PropertyType;
  setPropertyType: (p: PropertyType) => void;
  service: Service;
  hours: number | null; setHours: (n: number | null) => void;
  workers: number | null; setWorkers: (n: number | null) => void;
  floors: number | null; setFloors: (n: number | null) => void;
  aptRooms: number | null; setAptRooms: (n: number | null) => void;
  aptBathrooms: number | null; setAptBathrooms: (n: number | null) => void;
  aptLivingRooms: number | null; setAptLivingRooms: (n: number | null) => void;
  aptKitchens: number | null; setAptKitchens: (n: number | null) => void;
  isFurnished: boolean | null; setIsFurnished: (b: boolean | null) => void;
  chaletRooms: number | null; setChaletRooms: (n: number | null) => void;
  addOnServices: Service[];
  selectedAddOns: Record<string, number>;
  setAddOnQty: (id: string, qty: number) => void;
  showError: (cond: boolean) => boolean;
  t: (k: string) => string;
  isRTL: boolean;
  cur: string;
  fmt: (n: number) => string;
}

function ServiceConfigPanel(props: ServiceConfigProps) {
  const { category, service, t, isRTL, cur, fmt } = props;
  const maxRooms = service.priceTiers ? Math.max(...service.priceTiers.map((ti) => ti.maxRooms)) : 10;

  return (
    <div className="space-y-4">
      {/* Hourly */}
      {category === "hourly" && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <Stepper label={`${t("bookingClean.hours")} (${service.minHours ?? 1}-${service.maxHours ?? 8})`} value={props.hours} setValue={props.setHours} min={service.minHours ?? 1} max={service.maxHours ?? 8} error={props.showError(props.hours == null)} />
            <Stepper label={`${t("bookingClean.workers")} (${service.minWorkers ?? 1}-${service.maxWorkers ?? 1})`} value={props.workers} setValue={props.setWorkers} min={service.minWorkers ?? 1} max={service.maxWorkers ?? 5} error={props.showError(props.workers == null)} />
          </div>
          {(props.showError(props.hours == null) || props.showError(props.workers == null)) && (
            <p className="text-xs text-red-600 dark:text-red-400">
              {props.showError(props.hours == null) ? t("bookingClean.validation.hoursRequired") : t("bookingClean.validation.workersRequired")}
            </p>
          )}
        </>
      )}

      {/* Deep house */}
      {category === "deep" && props.propertyType === "house" && (
        <Stepper label={t("bookingClean.floors")} value={props.floors} setValue={props.setFloors} min={service.minFloors ?? 1} max={service.maxFloors ?? 5} error={props.showError(props.floors == null)} />
      )}

      {/* Deep apartment */}
      {category === "deep" && props.propertyType === "apartment" && (() => {
        const items = service.apartmentPriceItems ?? [];
        // Steppers roam freely inside the table's outer bounds; exact-match
        // gaps inside those bounds surface via the "combination unavailable"
        // alert below instead of blocked buttons.
        const maxAptRooms = items.length > 0 ? Math.max(...items.map((it) => it.rooms)) : 10;
        const maxAptBathrooms = items.length > 0 ? Math.max(...items.map((it) => it.bathrooms)) : 10;
        const maxAptLivingRooms = items.length > 0 ? Math.max(...items.map((it) => it.livingRooms)) : 10;
        const maxAptKitchens = items.length > 0 ? Math.max(...items.map((it) => it.kitchens)) : 10;
        const unitsSelected = (props.aptRooms ?? 0) + (props.aptBathrooms ?? 0) + (props.aptLivingRooms ?? 0) + (props.aptKitchens ?? 0) > 0;
        const comboItem = items.find(
          (it) =>
            it.rooms === (props.aptRooms ?? 0) &&
            it.bathrooms === (props.aptBathrooms ?? 0) &&
            it.livingRooms === (props.aptLivingRooms ?? 0) &&
            it.kitchens === (props.aptKitchens ?? 0)
        );
        const comboUnavailable = unitsSelected && !comboItem;
        // Highlight the steppers red only while NO unit has been selected yet.
        const unitError = props.showError(!unitsSelected);
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Stepper label={t("bookingClean.rooms")} value={props.aptRooms} setValue={props.setAptRooms} min={0} startAt={1} max={maxAptRooms} error={unitError} />
              <Stepper label={t("bookingClean.bathrooms")} value={props.aptBathrooms} setValue={props.setAptBathrooms} min={0} startAt={1} max={maxAptBathrooms} error={unitError} />
              <Stepper label={t("bookingClean.livingRooms")} value={props.aptLivingRooms} setValue={props.setAptLivingRooms} min={0} startAt={1} max={maxAptLivingRooms} error={unitError} />
              <Stepper label={t("bookingClean.kitchens")} value={props.aptKitchens} setValue={props.setAptKitchens} min={0} startAt={1} max={maxAptKitchens} error={unitError} />
            </div>
            {unitError && (
              <p className="text-xs text-red-600 dark:text-red-400">{t("bookingClean.atLeastOneUnit")}</p>
            )}
            {comboUnavailable && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 dark:bg-red-950/30 dark:border-red-900/50">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-800 dark:text-red-200">{t("bookingClean.comboUnavailable")}</p>
              </div>
            )}
            {/* Furnished/Unfurnished only appears after at least one unit is selected */}
            {unitsSelected && (
              <div>
                <label className={cn("text-sm font-medium block mb-2", props.showError(props.isFurnished == null) && "text-red-600 dark:text-red-400")}>{t("bookingClean.furnished")}</label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => props.setIsFurnished(true)} className={cn("p-3 rounded-xl border-2 font-medium transition-colors", props.isFurnished === true ? "border-primary bg-primary/5 text-primary" : props.showError(props.isFurnished == null) ? "border-red-500 text-red-600 dark:text-red-400" : "border-border")}>{t("bookingClean.furnishedYes")}</button>
                  <button type="button" onClick={() => props.setIsFurnished(false)} className={cn("p-3 rounded-xl border-2 font-medium transition-colors", props.isFurnished === false ? "border-primary bg-primary/5 text-primary" : props.showError(props.isFurnished == null) ? "border-red-500 text-red-600 dark:text-red-400" : "border-border")}>{t("bookingClean.furnishedNo")}</button>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Chalet */}
      {category === "chalet" && (
        <Stepper label={`${t("bookingClean.rooms")} (1-${maxRooms})`} value={props.chaletRooms} setValue={props.setChaletRooms} min={1} max={maxRooms} error={props.showError(props.chaletRooms == null)} />
      )}

      {/* Add-ons (hourly equipment toggle / chalet laundry quantity) */}
      {props.addOnServices.length > 0 && (
        <div>
          <label className="text-sm font-medium block mb-2">{category === "chalet" ? t("bookingClean.laundryAddOns") : t("bookingClean.equipmentAddOns")}</label>
          <div className="space-y-2">
            {props.addOnServices.map((addOn) => {
              const qty = props.selectedAddOns[addOn.id] ?? 0;
              const supportsQty = addOn.supportsQuantity === true;
              const lineTotal = addOn.price * (supportsQty ? qty : (qty > 0 ? 1 : 0));
              const rowCls = cn("p-3 rounded-xl border flex items-center justify-between transition-colors", qty > 0 ? "border-primary bg-primary/5" : "border-border");
              const label = (
                <div className="flex-1">
                  <p className="text-sm font-medium">{isRTL ? addOn.nameAr : addOn.nameEn}</p>
                  <p className="text-xs text-muted-foreground">
                    {supportsQty && qty > 0
                      ? `${qty} × ${fmt(addOn.price)} = ${fmt(lineTotal)} ${cur}`
                      : `+ ${fmt(addOn.price)} ${cur}`}
                  </p>
                </div>
              );
              if (supportsQty) {
                return (
                  <div key={addOn.id} className={rowCls}>
                    {label}
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => props.setAddOnQty(addOn.id, Math.max(0, qty - 1))} className="w-8 h-8 flex items-center justify-center rounded-lg border border-input hover:bg-muted">
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="font-bold w-6 text-center">{qty}</span>
                      <button type="button" onClick={() => props.setAddOnQty(addOn.id, qty + 1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-input hover:bg-muted">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              }
              return (
                <button
                  key={addOn.id}
                  type="button"
                  onClick={() => props.setAddOnQty(addOn.id, qty > 0 ? 0 : 1)}
                  className={cn(rowCls, "w-full text-start hover:border-primary/50")}
                >
                  {label}
                  <span className={cn("w-8 h-8 rounded-lg border-2 flex items-center justify-center flex-shrink-0", qty > 0 ? "bg-primary border-primary" : "border-input")}>
                    {qty > 0 && <CheckCircle2 className="h-4 w-4 text-primary-foreground" />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chalet date picker (month calendar) — removed; now uses inline horizontal strip
// ---------------------------------------------------------------------------
// eslint-disable-next-line
